'use client'
import { Drawer, Dialog } from '@/CommonComponents'
import type { ReactElement, ReactNode } from 'react'
import React, { createContext, useCallback, useContext, useState } from 'react'

export interface IModalContextType {
  showDrawer: (drawerContent: ReactElement<any>) => void
  showDialog: (dialogContent: ReactElement<any>, dialogTitle: string) => void
  closeDrawer: () => void
  closeDialog: () => void
}

interface IDrawerState {
  drawerContent: ReactElement<any> | null
  show: boolean
}

interface IDialogState {
  dialogContent: ReactElement<any> | null
  show: boolean
  dialogTitle: string
}

const modalContext = createContext<IModalContextType | undefined>(undefined)

export const useModal = (): IModalContextType => {
  const context = useContext(modalContext)
  if (!context) {
    throw new Error('useModal must be used within a ModalProvider')
  }
  return context
}

export const useDrawer = (): ((drawerContent: ReactElement<any>) => void) => {
  const { showDrawer } = useModal()
  return showDrawer
}

export const useDialog = (): ((dialogContent: ReactElement<any>, dialogTitle: string) => void) => {
  const { showDialog } = useModal()
  return showDialog
}

export default function ModalProvider({
  children,
}: Readonly<{
  children: ReactNode
}>) {
  const [drawer, setDrawer] = useState<IDrawerState>({ drawerContent: null, show: false })
  const [dialog, setDialog] = useState<IDialogState>({ dialogTitle: '', dialogContent: null, show: false })

  const showDrawer = useCallback((drawerContent: ReactElement<any>) => {
    setDrawer({ drawerContent, show: true })
  }, [])

  const showDialog = useCallback((dialogContent: ReactElement<any>, dialogTitle: string) => {
    setDialog({ dialogContent, show: true, dialogTitle })
  }, [])

  const closeDrawer = useCallback(() => {
    setDrawer({ drawerContent: null, show: false })
  }, [])

  const closeDialog = useCallback(() => {
    setDialog({ dialogContent: null, show: false, dialogTitle: '' })
  }, [])

  const contextValue: IModalContextType = {
    showDrawer,
    showDialog,
    closeDrawer,
    closeDialog,
  }

  return (
    <modalContext.Provider value={contextValue}>
      {children}
      <Drawer open={drawer.show} anchor="right" onClose={closeDrawer}>
        {drawer.drawerContent && React.cloneElement(drawer.drawerContent, { onClose: closeDrawer })}
      </Drawer>
      <Dialog title={dialog.dialogTitle} open={dialog.show} onClose={closeDialog}>
        {dialog.dialogContent && React.cloneElement(dialog.dialogContent, { onClose: closeDialog })}
      </Dialog>
    </modalContext.Provider>
  )
}
